// src/context/AppContext.jsx
// Backward-compatible shim — aggregates all sub-contexts so existing
// useApp() consumers continue working unchanged.

import { useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useProjects } from './ProjectsContext';
import { useLeads } from './LeadsContext';
import { usePortfolio } from './PortfolioContext';
import { useUI } from './UIContext';
import { useTeamSettings } from './TeamSettingsContext';
import { formatCurrency, calculateCarryCosts, calculateROI } from '../utils/calculations';
import { getStatusBadge } from '../utils/statusBadges';
import { NAV_ITEMS } from '../config/navigation';

export function useApp() {
  const auth = useAuth();
  const projects = useProjects();
  const leads = useLeads();
  const portfolio = usePortfolio();
  const ui = useUI();
  const settings = useTeamSettings();

  // Wrap in useMemo so consumers only re-render when their specific sub-context changes.
  // Pure utilities (formatCurrency etc.) are module-level constants — stable references.
  // NAV_ITEMS is a module-level constant — stable reference, no new array each render.
  return useMemo(
    () => ({
      ...auth,
      ...projects,
      ...leads,
      ...portfolio,
      ...ui,
      ...settings,
      // Pure utilities (no state, just re-exported for backward compatibility)
      formatCurrency,
      getStatusBadge,
      calculateCarryCosts,
      calculateROI,
      navItems: NAV_ITEMS,
    }),
    [auth, projects, leads, portfolio, ui, settings]
  );
}
